import { Component, h } from "preact";
import {
  IImageMeta,
  IOption,
  IReactConfiguration,
  ISizeControllerState,
} from "../../../typings";

import Dropzone from "../../modules/Dropzone";
import Uploader from "../../uploader";
import OutputController from "../../../shared/controller/output-controller";
import SizeController from "../../../shared/controller/size-controller";
import UnitSystemController from "../../../shared/controller/unit-system-controller";
import {
  suportedFiles,
  suportedFilesWithoutConversion,
} from "../../../shared/configuration";
import ColorController from "../../../shared/controller/color-controller";
import ExternalController from "../../../shared/controller/external-controller";
import LabelSvgSprite from "../label-svg-sprite";
import TemplateController from "../../../shared/controller/template-controller";
import IEventController from "../../../shared/controller/ievent-controller";

export default class ImageUploader extends Component<
  IImageUploaderProps,
  IImageUploaderState
> {
  height: number;
  width: number;
  dropzone: Dropzone;

  constructor() {
    super();

    this.onDrop = this.onDrop.bind(this);
    this.openDropzone = this.openDropzone.bind(this);
    this.bindDropzone = this.bindDropzone.bind(this);
    // this.close = this.close.bind(this);
  }

  onDrop(acceptedFiles: FileList): void {
    this.props.main.clearMessage();
    if (acceptedFiles) {
      const file: File = acceptedFiles[0];
      if (file.size > 50000000) {
        this.props.main.setMessage(
          this.props.option.meta.fields.sizeFileToLargeError.replace(
            "%1",
            "50",
          ),
        );
      } else {
        this.setState({ isLoading: true }, (): void => {
          this.props.evenController.dispatchEvent(
            this.props.evenController.newEvent("startLoading"),
          );
        });
        const reader: FileReader = new FileReader();
        reader.addEventListener("load", (fileLoadedEvent: ProgressEvent) => {
          const meta: IImageMeta = {} as IImageMeta;
          const image: string = fileLoadedEvent.target.result;

          if (image === "data:") {
            this.setState({ isLoading: false }, () => {
              this.props.evenController.dispatchEvent(
                this.props.evenController.newEvent("stopLoading"),
              );
              throw "The uploaded file could not be converted to base64";
            });
          }

          this.props.externalController
            .uploadImage(image)
            .then((url: string) => {
              const state: ISizeControllerState =
                this.props.sizeController.getState();
              const scale: number = this.props.sizeController.getScale();
              if (suportedFilesWithoutConversion.indexOf(file.type) !== -1) {
                this.uploadSupportedFile(file, url, meta)
                  .then(() => {
                    this.uploadFile(file, url, meta, url);
                  })
                  .catch((e: any) => {
                    this.setState({ isLoading: false }, () => {
                      this.props.evenController.dispatchEvent(
                        this.props.evenController.newEvent("stopLoading"),
                      );
                      this.props.externalController.setMessage(
                        this.props.templateController.getTemplate().meta.fields
                          .errorFileCouldNotBeUploaded,
                      );
                      this.props.sizeController.restoreState(state);
                    });
                    throw e;
                  });
                return;
              }

              this.props.externalController
                .convertImage(url, file.name)
                .then((pngUrl: string) => {
                  this.props.externalController
                    .getSizeOfImage(pngUrl)
                    .then((imageSize: number) => {
                      const image: any = new Image();
                      if (imageSize >= 50000000) {
                        this.setState({ isLoading: false }, () => {
                          this.props.evenController.dispatchEvent(
                            this.props.evenController.newEvent("stopLoading"),
                          );
                          this.props.sizeController.restoreState(state);
                        });
                        this.props.main.setMessage(
                          this.props.option.meta.fields.sizeFileToLargeAfterConversionError.replace(
                            "%1",
                            "5",
                          ),
                        );
                        return;
                      }
                      image.addEventListener("load", () => {
                        const canvas: HTMLCanvasElement =
                          document.createElement("canvas");
                        canvas.width = image.width;
                        canvas.height = image.height;
                        const ctx: CanvasRenderingContext2D =
                          canvas.getContext("2d");
                        ctx.drawImage(image, 0, 0);

                        this.upload(canvas.toDataURL(), file, meta, url, scale);
                      });
                      image.crossOrigin = "anonymous";
                      image.src = pngUrl;
                    });
                })
                .catch((e: any) => {
                  this.setState({ isLoading: false }, () => {
                    this.props.evenController.dispatchEvent(
                      this.props.evenController.newEvent("stopLoading"),
                    );
                    this.props.main.setMessage(
                      this.props.templateController.getTemplate().meta.fields
                        .errorFileCouldNotBeUploaded,
                    );
                    throw e;
                  });
                });
            })
            .catch((e: any) => {
              this.setState({ isLoading: false }, () => {
                this.props.evenController.dispatchEvent(
                  this.props.evenController.newEvent("stopLoading"),
                );
                this.props.main.setMessage(
                  this.props.templateController.getTemplate().meta.fields
                    .errorFileCouldNotBeUploaded,
                );
                throw e;
              });
            });
        });
        reader.readAsDataURL(file);
      }
    }
  }

  upload(pngBase64: string, file: File, meta: IImageMeta, url: string): void {
    this.props.externalController
      .uploadImage(pngBase64)
      .then((pngUrl: string) => {
        this.uploadSupportedFile(file, pngUrl, meta)
          .then(() => {
            this.uploadFile(file, pngUrl, meta, url);
          })
          .catch((e: any) => {
            this.setState({ isLoading: false }, () => {
              this.props.evenController.dispatchEvent(
                this.props.evenController.newEvent("stopLoading"),
              );
              this.props.externalController.setMessage(
                this.props.templateController.getTemplate().meta.fields
                  .errorFileCouldNotBeUploaded,
              );
            });
            throw e;
          });
      });
  }

  bindDropzone(dropzone: Dropzone): void {
    this.dropzone = dropzone;
  }

  openDropzone(): void {
    this.dropzone.open();
  }

  getFixedWidth(): undefined {
    const optionWidth: IOption =
      this.props.templateController.getOptionByCode("width");
    return optionWidth.attributes.fixed
      ? optionWidth.attributes.fixed.value
      : undefined;
  }

  getFixedHeight(): undefined {
    const optionHeight: IOption =
      this.props.templateController.getOptionByCode("height");
    return optionHeight.attributes.fixed
      ? optionHeight.attributes.fixed.value
      : undefined;
  }

  // close(): void {
  //   this.props.onClose();
  // }

  render(): JSX.Element {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Dropzone
          onDrop={this.onDrop}
          className={"preview__image"}
          multiple={false}
          accept={suportedFiles.join(", ")}
          bind={this.bindDropzone}
        >
          <LabelSvgSprite
            cssClass="h-24 mx-auto"
            viewBox="0 0 64 64"
            imageCode={"upload"}
            multicolorImage={false}
            conf={this.props.conf}
          />
          <div className="my-6">
            <h3 className="text-2xl font-bold text-black">
              {this.state.stateImage === true
                ? this.props.option.meta.fields.panelLabelPreview
                : this.props.option.meta.fields.panelPreUploadImage}
            </h3>
            <span className="text-xl text-gray-dark">
              {this.props.option.meta.description
                .replace(
                  "%width%",
                  this.props.unitSystemController.convertUnitWithSystem(
                    this.getFixedWidth(),
                  ),
                )
                .replace(
                  "%height%",
                  this.props.unitSystemController.convertUnitWithSystem(
                    this.getFixedHeight(),
                  ),
                )}
            </span>
          </div>
        </Dropzone>

        <button
          className="table p-3 mx-auto font-bold text-white rounded-md bg-blue"
          onClick={this.openDropzone}
        >
          {this.props.option.meta.fields.uploaderBrowseButton}
        </button>

        {this.state.isLoading && (
          <div
            id="checkout-loader"
            data-role="checkout-loader"
            className="loading-mask"
            data-mage-init='{"checkoutLoader": {}}'
          >
            <div className="loader loader--visible">
              <div className="loader__circle" />
            </div>
          </div>
        )}
      </div>
    );
  }

  private updateFileFronOutputValue(): void {
    this.props.outputController
      .setValue(["file-front", "file-front"], "upload")
      .then(() => {})
      .catch(() => {});
  }

  uploadSupportedFile(file: File, url: string, meta: IImageMeta): Promise<any> {
    return new Promise((resolve: Function, reject: Function) => {
      //Load image to get pixel size
      const image: any = new Image();
      image.addEventListener("load", () => {
        if (image.height < 95 || image.width < 95) {
          this.props.main.setMessage(
            this.props.option.meta.fields.sizeImageToSmallError.replace(
              "%s",
              "95",
            ),
          );
          reject();
        } else {
          meta.height = image.height;
          meta.width = image.width;

          resolve();
        }
      });
      image.addEventListener("error", () => {
        this.props.main.setMessage(
          this.props.templateController.getTemplate().meta.fields
            .errorFileCouldNotBeUploaded,
        );
        reject();
      });
      image.crossOrigin = "anonymous";
      image.src = url;
    });
  }

  private uploadFile(
    file: File,
    pngUrl: string,
    meta: IImageMeta,
    url: string,
  ): void {
    meta.name = file.name;
    meta.type = file.type;
    meta.size = file.size;

    this.props.outputController.startEventTransaction();

    this.props.outputController
      .setValue(["image", this.props.originalMetaName], meta)
      .then(() => {})
      .catch(() => {});

    this.props.outputController
      .setValue(["image", this.props.originalName], url)
      .then(() => {})
      .catch(() => {});

    this.props.outputController
      .setValue(["image", this.props.originalPngName], pngUrl)
      .then(() => {})
      .catch(() => {});

    const canvas: HTMLCanvasElement = document.createElement("canvas");
    const imageElement: HTMLImageElement = new Image();
    imageElement.addEventListener("load", () => {
      canvas.width = imageElement.width;
      canvas.height = imageElement.height;
      const context: CanvasRenderingContext2D = canvas.getContext("2d");
      context.drawImage(imageElement, 0, 0);
      this.props.colorController.updateColors(canvas.toDataURL()).catch(() => {
        this.props.main.setMessage(
          this.props.templateController.getTemplate().meta.fields
            .errorColorsCouldNotBeExtracted,
        );
      });
    });
    imageElement.crossOrigin = "anonymous";
    imageElement.src = pngUrl;

    const imageData: Cropper.Data =
      this.props.sizeController.generateCroppedData(meta);

    this.props.sizeController
      .newImageCroppedData(imageData, meta)
      .then(() => {
        this.updateFileFronOutputValue();

        this.setState({ isLoading: false }, () => {
          this.props.evenController.dispatchEvent(
            this.props.evenController.newEvent("stopLoading"),
          );
          this.props.outputController.commitEventTransaction();
        });
      })
      .catch(() => {
        this.updateFileFronOutputValue();

        this.setState({ isLoading: false }, () => {
          this.props.evenController.dispatchEvent(
            this.props.evenController.newEvent("stopLoading"),
          );
          this.props.outputController.commitEventTransaction();
        });
      });
  }
}

interface IImageUploaderProps {
  main: Uploader;
  onClose: Function;
  conf: IReactConfiguration;

  outputController: OutputController;
  sizeController: SizeController;
  colorController: ColorController;
  externalController: ExternalController;
  templateController: TemplateController;
  unitSystemController: UnitSystemController;
  evenController: IEventController;

  originalName: string;
  originalMetaName: string;
  originalPngName: string;
  croppedDataName: string;

  option: IOption;
}

interface IImageUploaderState {
  isLoading: boolean;
}
